import { ComponentProps, DOMAttributes, useState } from 'react';

import { SeoHead } from '@components/SeoHead';
import MySnackbar, { MySnackbarProps } from '@components/snackbar/Snackbar';

import useTranslation from '@hooks/useTranslation';
import { PageLayout } from '@layout';
import { Paper, lighten, useMediaQuery, useTheme } from '@mui/material';
import { Box, Button, TextField, Typography } from '@mui/material';

import { colors } from 'theme';
import { ZodError, z } from 'zod';

type OnChange = ComponentProps<typeof TextField>['onChange'];
type OnSubmit = DOMAttributes<Element>['onSubmit'];

export default function ContactPage() {
    const { t } = useTranslation('library');
    const [showSnackbar, setShowSnackbar] = useState<MySnackbarProps>({
        message: '',
        show: false,
    });
    const theme = useTheme();
    const defaultForm = {
        fullName: '',
        email: '',
        message: '',
    };
    const [form, setForm] = useState(defaultForm);
    const [errors, setErrors] = useState(defaultForm);
    const [loading, setLoading] = useState(false);

    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const formData = z.object({
        fullName: z.string().min(1, t('required_field')),
        email: z.string().email({ message: t('invalid_email') }),
        message: z.string().min(1, t('required_field')),
    });

    const handleChange: OnChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
        setErrors({ ...errors, [e.target.name]: '' });
    };

    const handleSubmit: OnSubmit = async () => {
        setLoading(true);
        try {
            const parsedForm = formData.parse(form);
            const res = await fetch('/api/alerting/', {
                method: 'POST',
                body: JSON.stringify({
                    fullName: parsedForm.fullName,
                    subject: parsedForm.email,
                    description: parsedForm.message,
                    url: window.location.pathname,
                    source: 'contact-us',
                    type: 'contact-form',
                    elem_id: 'contact-form',
                }),
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (res.ok) {
                setShowSnackbar({
                    show: true,
                    severity: 'success',
                    message: t('email_sent_successfully'),
                });
                // clear inputs on success
                setForm(defaultForm);
            } else {
                setShowSnackbar({
                    show: true,
                    message: t('report_failed'),
                    severity: 'error',
                });
            }
        } catch (error) {
            if (error instanceof ZodError) {
                const issues = error.issues;
                let errors = defaultForm;
                issues.forEach((issue) => {
                    errors = { ...errors, [issue.path[0]]: issue.message };
                });
                setErrors(errors);
            }
        } finally {
            setLoading(false);
        }
    };
    return (
        <>
            <SeoHead />
            <PageLayout>
                <Paper
                    elevation={0}
                    variant={isMobile ? 'elevation' : 'outlined'}
                    sx={{
                        padding: { xs: '10px', md: '30px' },
                        width: '100%',
                    }}
                >
                    <Box
                        display="flex"
                        flexDirection="column"
                        gap={2}
                        sx={{
                            '& .MuiFormHelperText-root': {
                                textAlign: 'start',
                            },
                        }}
                    >
                        <Typography variant="body1" textAlign="center">
                            {t('report_an_error_description')}
                        </Typography>
                        <TextField
                            placeholder={t('label_full_name')}
                            name="fullName"
                            value={form.fullName}
                            onChange={handleChange}
                            error={!!errors.fullName}
                            helperText={errors.fullName}
                        />
                        <TextField
                            placeholder={t('email')}
                            name="email"
                            value={form.email}
                            onChange={handleChange}
                            error={!!errors.email}
                            helperText={errors.email}
                        />
                        <TextField
                            multiline
                            rows={4}
                            placeholder={t('your_message')}
                            name="message"
                            value={form.message}
                            onChange={handleChange}
                            error={!!errors.message}
                            helperText={errors.message}
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            sx={{
                                color: colors.primary,
                                backgroundColor: colors.secondary,
                                py: 1,
                                width: '100px',
                                margin: '0 auto',
                                '&:hover': {
                                    backgroundColor: lighten(
                                        colors.secondary,
                                        0.2,
                                    ),
                                },
                            }}
                            disabled={loading}
                            onClick={handleSubmit}
                        >
                            {t('send')}
                        </Button>
                    </Box>
                </Paper>
                {showSnackbar && (
                    <MySnackbar
                        anchorOrigin={{
                            vertical: isMobile ? 'top' : 'bottom',
                            horizontal: isMobile ? 'center' : 'left',
                        }}
                        message={showSnackbar.message}
                        severity={showSnackbar.severity}
                        key="top-center"
                        sx={{ mx: { xs: '20%', sm: 'unset' } }}
                        open={showSnackbar.show}
                        onClose={() => {
                            // first hide it, then delete the message
                            // to reduce visual artifact, since removing the message makes it zero width
                            // then it hides, so looks weird
                            setShowSnackbar({ ...showSnackbar, show: false });
                            setTimeout(
                                () =>
                                    setShowSnackbar({
                                        message: '',
                                        show: false,
                                    }),
                                300,
                            );
                        }}
                        autoHideDuration={3000}
                    />
                )}
            </PageLayout>
        </>
    );
}
